import React, { useState, useEffect, lazy, Suspense } from "react"
import { useTranslation } from "react-i18next"
import { ThemeProvider, StyledEngineProvider, createTheme } from "@mui/material/styles" // adaptV4Theme
import axios from "axios"

import { AccessContextProvider } from "../contexts/AccessContext"
import { RouteStoreProvider } from "@/features/route/state/RouteStore.jsx"
import { ChangeFeedProvider } from "@/features/changeFeed"
import { WeatherRunProvider } from "@/features/weatherRun"
import environment from "../config/environment"
import MainComponent from "./MainComponent"
import { TourProvider } from "@/features/tutorials"
import LoginForm from "../features/auth/LoginForm"
import theme from "../theme/theme"
import postGraphile from "../services/postGraphile"
import { checkAndRefreshToken } from "../services/auth"
import { isDemoMode } from "../utils/appNow"

import "./Application.css"

const APP_VERSION = import.meta.env.VITE_APP_VERSION
const APP_NAME = import.meta.env.VITE_APP_NAME

const applicationTheme = createTheme(theme) //adaptV4Theme(theme))

// Test-Gate für den benchmap-Vollbild-Preview; lazy hält benchmap aus dem Entry-Bundle
const BENCHMAP_PREVIEW = new URLSearchParams(window.location.search).has(
  "benchmap",
)
const BenchmapPreview = lazy(
  () => import("@/features/weatherlayers/BenchmapPreview.jsx"),
)

function buildUiCapabilities(payload) {
  const access = Array.isArray(payload?.access) ? payload.access : []

  return {
    canSeeFleetOnline: access.includes("fleet_ardmore"),
    canSeeRnwAnalyser: access.includes("visibility_rnw_analyser=true"),
    canSeeErklaerbaer: access.includes("visibility_erklaerbaer=true"),
  }
}

function buildConfig(payload, dataApi) {
  let config = {
    // TODO Vollzugriff auf alle Daten bei folgenden Rechten vs Sicherheit? (2022-03-16)
    customerIdFix:
      payload.access.includes("sc_service") || payload.access.includes("sc_all") ? undefined : payload["customerId"],
  }
  return config
}

const Application = () => {
  const { t } = useTranslation()
  const [access, setAccess] = useState({ loggedIn: false })
  const [error, setError] = useState({ msg: "" })
  const [appl] = useState({ title: APP_NAME, version: APP_VERSION })

  useEffect(() => {
    document.title = appl.title
  }, [appl])

  async function login(credentials) {
    let api = axios.create({ baseURL: environment.auth.url, withCredentials: true }) //withCredentials: true --> führt momentan zu CORS error
    let accessToken = undefined
    let status = undefined
    let payload = undefined
    let config = undefined
    let pages = undefined
    await api
      .post(environment.auth.path, { name: credentials.name, password: credentials.password })
      .then((result) => {
        accessToken = result.data.accessToken
        let splitted = accessToken.split(".")
        if (splitted.length === 3) {
          payload = JSON.parse(atob(splitted[1]))
          // Check if access_key 'meteotracker' is available
          if (!payload.access.includes("meteotracker")) {
            setError((currentError) => ({ ...currentError, msg: t("login_failed") }))
            status = 500
            setAccess({ loggedIn: false })
            return
          }
          // Kunden Daten aus der SC-Datenbank
          let customerPromise = new Promise((resolve, reject) => {
            const query =
              "{ customers (condition: {id: " +
              payload["customerId"] +
              "}) { nodes {firstName,lastName,organisationName }} }"
            postGraphile
              .fetchRequest(
                environment.sc_api.url,
                postGraphile.httpRequestData(query, accessToken),
                "customer data after login, customer id: " + payload["customerId"]
              )
              .then((response) => {
                if (response?.data?.customers?.nodes?.length === 1) {
                  resolve(response.data.customers.nodes[0])
                } else {
                  reject("customer not found")
                }
              })
              .catch((err) => {
                reject("customer error: " + err)
              })
          })

          Promise.all([customerPromise]).then(
            async (values) => {
              // customerPromise result: customer data
              let customer = values[0]
              payload["customerName"] = customer.organisationName
                ? customer.organisationName
                : customer.firstName + customer.lastName

              config = buildConfig(payload, null)
              const uiCapabilities = buildUiCapabilities(payload)
              setError((currentError) => ({ ...currentError, msg: "" }))
              status = 200
              if (isDemoMode) await demoReset(accessToken)
              setAccess((currentAccess) => ({
                ...currentAccess,
                configuration: config,
                environment: environment,
                pages: pages,
                uiCapabilities,
                loggedIn: true,
                status: status,
                token: accessToken,
                payload: payload,
              }))
            },
            (reason) => {
              // TODO für produktiv Betrieb err ändern / rausnehmen
              // setError( (currentError) => ( {...currentError, msg: t("login_failed_sorry") + " (" + reason + ")"} ) )
              setError((currentError) => ({ ...currentError, msg: t("login_failed_sorry") }))
              status = 500
            }
          )
        } else {
          // TODO für produktiv Betrieb err ändern / rausnehmen
          // let err = "invalid access token"
          // console.log(err)
          // setError( (currentError) => ( {...currentError, msg: t("login_failed") + " (" + err + ")"} ) )
          setError((currentError) => ({ ...currentError, msg: t("login_failed") }))
          status = 500
        }
      })
      .catch((err) => {
        setError((currentError) => ({ ...currentError, msg: t("login_failed") }))
        if (err.response) {
          // console.log(err)
          status = err.response.status
        } else {
          status = 500
        }
      })
  }

  // Demo-Umgebung: Fixture-Zustand beim Login (jede Vorführung startet sauber) und beim Logout
  async function demoReset(token) {
    await axios
      .post(`${environment.core_api.url}/demo/reset`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .catch((e) => console.error("Demo reset failed:", e))
  }

  async function logout() {
    try {
      if (isDemoMode) {
        const token = (await checkAndRefreshToken(access, setAccess)) ?? access.token
        await demoReset(token)
      }
      await axios.post(`${environment.auth.url}/auth/logout`, {}, { withCredentials: true })
    } catch (error) {
      console.error("Logout failed:", error)
    } finally {
      setAccess({ loggedIn: false })
    }
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={applicationTheme}>
        {access.loggedIn ? (
          <AccessContextProvider values={access}>
            <ChangeFeedProvider>
              <WeatherRunProvider>
                <RouteStoreProvider>
                  {/* <LandingPage logout={logout} version={appl.version} /> */}
                  {BENCHMAP_PREVIEW ? (
                    <Suspense fallback={null}>
                      <BenchmapPreview />
                    </Suspense>
                  ) : (
                    <TourProvider>
                      <MainComponent logout={logout} />
                    </TourProvider>
                  )}
                </RouteStoreProvider>
              </WeatherRunProvider>
            </ChangeFeedProvider>
          </AccessContextProvider>
        ) : (
          <LoginForm login={login} error={error} />
        )}
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default Application
